import React, { useState } from 'react'
import { useGetRolesAllRoutesQuery } from '../../../redux/api/rolesApi/roles'
import { Button, Checkbox, Input, Spin, Table } from 'antd'
import {
	EntRoleAvailableRoutes,
	EntRoleAvailableRoutesResponse,
} from '../../../models/rolesAvailableRoutes'
import i18n from 'i18next'
import { useTranslation } from 'react-i18next'

interface RolesAvailableRoutesProps {
	selectedRoutes: EntRoleAvailableRoutes[]
	onRouteChange: (selectedRoutes: EntRoleAvailableRoutes[]) => void
}

export const RolesAvailableRoutes: React.FC<RolesAvailableRoutesProps> = ({
	selectedRoutes,
	onRouteChange,
}) => {
	const [description, setDescription] = useState('')
	const { data, isLoading, error } = useGetRolesAllRoutesQuery(description)
	const [selectAll, setSelectAll] = useState(false)
	const [isCommonSelected, setIsCommonSelected] = useState(false)

	const { t } = useTranslation()
	const predefinedAliases = [
		'/user',
		'/hb-territorial-unit',
		'/personnel-register',
		'/user/change-password',
		'/financial-indicators-register',
		'/register-movable-property',
		'/real-estate-register',
		'/report',
		'/hb-checkpoint',
		'/hb-citizenship',
	]

	type RouteMethod = 'methodGet' | 'methodPost' | 'methodPut' | 'methodDelete'

	const handleMethodChange = (
		record: EntRoleAvailableRoutesResponse,
		method: RouteMethod,
		checked: boolean
	) => {
		const updated = [...selectedRoutes]
		const existingIndex = updated.findIndex(r => r.routeId === record.id)

		if (existingIndex >= 0) {
			updated[existingIndex] = {
				...updated[existingIndex],
				[method]: checked,
			}
		} else {
			updated.push({
				id: null,
				routeId: record.id,
				methodGet: false,
				methodPost: false,
				methodPut: false,
				methodDelete: false,
				[method]: checked,
			})
		}

		onRouteChange(updated)
	}

	const handleSelectAll = () => {
		const newSelectAll = !selectAll
		setSelectAll(newSelectAll)

		const updated = roles.map(role => ({
			id: null,
			routeId: role.id,
			methodGet: newSelectAll,
			methodPost: newSelectAll,
			methodPut: newSelectAll,
			methodDelete: newSelectAll,
		}))

		onRouteChange(newSelectAll ? updated : []) // Если снимаем, то очищаем массив
	}

	const handleSetSpecificAliases = () => {
		const updated = [...selectedRoutes]

		if (!isCommonSelected) {
			// Выбрать общие
			roles.forEach(role => {
				if (predefinedAliases.includes(role.alias)) {
					const existingIndex = updated.findIndex(r => r.routeId === role.id)
					const newPermissions = {
						id: null,
						routeId: role.id,
						methodGet: true,
						methodPost: false,
						methodPut: false,
						methodDelete: false,
					}

					if (existingIndex >= 0) {
						updated[existingIndex] = { ...updated[existingIndex], ...newPermissions }
					} else {
						updated.push(newPermissions)
					}
				}
			})
			setIsCommonSelected(true)
		} else {
			// Убрать общие
			const filtered = updated.filter(r => {
				const role = roles.find(role => role.id === r.routeId)
				return !(role && predefinedAliases.includes(role.alias))
			})
			onRouteChange(filtered)
			setIsCommonSelected(false)
			return
		}

		onRouteChange(updated)
	}

	if (isLoading)
		return (
			<Spin spinning={true} style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }} />
		)
	if (error) return <div style={{ color: 'red', textAlign: 'center' }}>Ошибка загрузки данных</div>

	const roles: EntRoleAvailableRoutesResponse[] = data && Array.isArray(data.res) ? data.res : []

	const columns = [
		{
			title: '#',
			key: 'index',
			render: (_: any, __: any, index: number) => index + 1,
			width: 50,
		},
		{
			title: 'Описание',
			key: 'routeId',
			dataIndex: i18n.language === 'ru' ? 'descriptionRu' : 'descriptionKy',
		},
		{
			title: 'alias',
			key: 'alias',
			dataIndex: 'alias',
		},
		{
			title: <div style={{ textAlign: 'center' }}>Чтение</div>,
			key: 'methodGet',
			width: 120,
			render: (_: any, record: EntRoleAvailableRoutesResponse) => {
				const found = selectedRoutes.find(r => r.routeId === record.id)
				return (
					<div style={{ display: 'flex', justifyContent: 'center' }}>
						<Checkbox
							checked={found?.methodGet || false}
							onChange={e => handleMethodChange(record, 'methodGet', e.target.checked)}
						/>
					</div>
				)
			},
		},
		{
			title: <div style={{ textAlign: 'center' }}>Создание</div>,
			key: 'methodPost',
			width: 120,
			render: (_: any, record: EntRoleAvailableRoutesResponse) => {
				const found = selectedRoutes.find(r => r.routeId === record.id)
				return (
					<div style={{ display: 'flex', justifyContent: 'center' }}>
						<Checkbox
							checked={found?.methodPost || false}
							onChange={e => handleMethodChange(record, 'methodPost', e.target.checked)}
						/>
					</div>
				)
			},
		},
		{
			title: <div style={{ textAlign: 'center' }}>Редактирование</div>,
			key: 'methodPut',
			width: 120,
			render: (_: any, record: EntRoleAvailableRoutesResponse) => {
				const found = selectedRoutes.find(r => r.routeId === record.id)
				return (
					<div style={{ display: 'flex', justifyContent: 'center' }}>
						<Checkbox
							checked={found?.methodPut || false}
							onChange={e => handleMethodChange(record, 'methodPut', e.target.checked)}
						/>
					</div>
				)
			},
		},
		{
			title: <div style={{ textAlign: 'center' }}>Удаление</div>,
			key: 'methodDelete',
			width: 120,
			render: (_: any, record: EntRoleAvailableRoutesResponse) => {
				const found = selectedRoutes.find(r => r.routeId === record.id)
				return (
					<div style={{ display: 'flex', justifyContent: 'center' }}>
						<Checkbox
							checked={found?.methodDelete || false}
							onChange={e => handleMethodChange(record, 'methodDelete', e.target.checked)}
						/>
					</div>
				)
			},
		},
	]

	return (
		<div>
			<div style={{ marginBottom: 10 }}>
				<Input
					autoComplete='off'
					value={description}
					onChange={e => setDescription(e.target.value)}
					placeholder={t('general.search_by_description')}
					style={{ width: '450px', marginRight: 10 }}
				/>
				<Button onClick={handleSelectAll} style={{ marginRight: 10 }}>
					{selectAll ? t('general.unselect_all') : t('general.select_all')}
				</Button>
				<Button onClick={handleSetSpecificAliases}>
					{isCommonSelected ? t('general.unselect_general') : t('general.select_general')}
				</Button>
			</div>
			<div style={{ minHeight: '500px', maxHeight: '500px', overflowY: 'auto' }}>
				<Table columns={columns} dataSource={roles} rowKey='id' pagination={false} />
			</div>
		</div>
	)
}
